/**
 * Conversation API Service
 *
 * Client for assistant conversation persistence, trajectory linking, and export.
 */

import api from './api';

// =============================================================================
// Types
// =============================================================================

export interface Conversation {
  id: string;
  title: string | null;
  context_type: string;
  status: string;
  is_pinned: boolean;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string; // Base64 encoded
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  tool_calls: ToolCall[] | null;
  attachments: Attachment[] | null;
  sequence_number: number;
  created_at: string;
}

export interface ToolCall {
  name: string;
  input: Record<string, unknown>;
  result: Record<string, unknown>;
}

export interface Attachment {
  type: string;
  media_type?: string;
  storage_uri?: string;
}

export interface TrajectoryLink {
  trajectory_id: string;
  link_type: string;
  message_id: string | null;
  created_at: string;
}

export interface ConversationDetail extends Conversation {
  messages: Message[];
  linked_trajectories: TrajectoryLink[];
}

export interface ConversationListResponse {
  conversations: Conversation[];
  total: number;
}

export interface CreateConversationRequest {
  title?: string;
  context_type?: string;
  initial_message?: string; // Base64 encoded
  metadata?: Record<string, unknown>;
}

export interface UpdateConversationRequest {
  title?: string;
  is_pinned?: boolean;
  status?: 'active' | 'archived' | 'deleted';
}

export interface AddMessageRequest {
  role: 'user' | 'assistant';
  content: string; // Base64 encoded
  model?: string;
  input_tokens?: number;
  output_tokens?: number;
  tool_calls?: ToolCall[];
  attachments?: Attachment[];
  client_timestamp?: string;
}

export interface LinkTrajectoryRequest {
  trajectory_id: string;
  link_type?: 'reference' | 'debug' | 'created_from';
  message_id?: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List conversations for the current user.
 */
export async function listConversations(params: {
  status?: string;
  context_type?: string;
  search?: string;
  pinned_first?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<ConversationListResponse> {
  const response = await api.get('/assistant/conversations', { params });
  return response.data;
}

/**
 * Create a new conversation.
 */
export async function createConversation(
  request: CreateConversationRequest
): Promise<Conversation> {
  const response = await api.post('/assistant/conversations', request);
  return response.data;
}

/**
 * Get a conversation with messages and linked trajectories.
 */
export async function getConversation(
  conversationId: string,
  includeMessages: boolean = true,
  messageLimit: number = 100
): Promise<ConversationDetail> {
  const response = await api.get(`/assistant/conversations/${conversationId}`, {
    params: {
      include_messages: includeMessages,
      message_limit: messageLimit,
    },
  });
  return response.data;
}

/**
 * Update a conversation (title, pin status, or archive).
 */
export async function updateConversation(
  conversationId: string,
  updates: UpdateConversationRequest
): Promise<Conversation> {
  const response = await api.patch(`/assistant/conversations/${conversationId}`, updates);
  return response.data;
}

/**
 * Soft-delete a conversation.
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  await api.delete(`/assistant/conversations/${conversationId}`);
}

/**
 * Add a message to a conversation.
 */
export async function addMessage(
  conversationId: string,
  message: AddMessageRequest
): Promise<Message> {
  const response = await api.post(
    `/assistant/conversations/${conversationId}/messages`,
    message
  );
  return response.data;
}

/**
 * Link a trajectory to a conversation.
 */
export async function linkTrajectory(
  conversationId: string,
  request: LinkTrajectoryRequest
): Promise<TrajectoryLink> {
  const response = await api.post(
    `/assistant/conversations/${conversationId}/link-trajectory`,
    request
  );
  return response.data;
}

/**
 * Get all trajectories linked to a conversation.
 */
export async function getLinkedTrajectories(
  conversationId: string
): Promise<TrajectoryLink[]> {
  const response = await api.get(
    `/assistant/conversations/${conversationId}/trajectories`
  );
  return response.data;
}

/**
 * Export a conversation in the specified format.
 */
export async function exportConversation(
  conversationId: string,
  format: 'json' | 'markdown' = 'json'
): Promise<Blob> {
  const response = await api.get(
    `/assistant/conversations/${conversationId}/export`,
    {
      params: { format },
      responseType: 'blob',
    }
  );
  return response.data;
}

/**
 * Download an exported conversation.
 */
export function downloadConversation(
  blob: Blob,
  conversationId: string,
  format: 'json' | 'markdown'
): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `conversation-${conversationId.slice(0, 8)}.${format === 'json' ? 'json' : 'md'}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Decode base64 content to string.
 */
export function decodeContent(content: string): string {
  try {
    return atob(content);
  } catch {
    // If decoding fails, return as-is (might not be base64)
    return content;
  }
}

/**
 * Encode string content to base64.
 */
export function encodeContent(content: string): string {
  return btoa(content);
}
